import { Table } from '@tanstack/react-table';
import { Dropdown, DropdownMenuItemProps } from '../../Dropdown';
import { Icons } from '../../Icons';
import { ColumnGroup } from '../types';
import { getColumnId } from '../utils';
import { TableGlobalActionTrigger } from './TableGlobalActionTrigger';

const GlobalSortTool = <TData, TValue>({
  table,
  columnGroups,
}: {
  table: Table<TData>;
  columnGroups: ColumnGroup<TData, TValue>[];
}) => {
  const sortableFromConfig = columnGroups
    .flatMap((g) => g.columns)
    .filter((col) => col.sort?.shouldSort)
    .map((col) => getColumnId(col));

  const list = sortableFromConfig as string[];

  if (list.length === 0) return null;

  const sortingState = table.getState().sorting;
  const activeSorts = sortingState.filter((sort) => sort.id);
  const hasActiveSorts = activeSorts.length > 0;

  const clearAllSorts = () => {
    table.resetSorting();
  };

  const createSortMenuItems = () => {
    const items: DropdownMenuItemProps[] = [];

    list.forEach((colId) => {
      const col = table.getColumn(colId);
      const colDef = (col?.columnDef ?? {}) as {
        title?: string;
        meta?: {
          sortNode?: React.ReactNode;
          sortUi?: {
            asc?: {
              globalNode?: React.ReactNode;
              columnNode?: React.ReactNode;
              icon?: React.ReactNode;
            };
            desc?: {
              globalNode?: React.ReactNode;
              columnNode?: React.ReactNode;
              icon?: React.ReactNode;
            };
          };
        };
      };

      const isSorted =
        (table.getColumn(colId)?.getIsSorted?.() as 'asc' | 'desc' | false) ||
        false;

      items.push({
        label: String(
          (colDef.meta?.sortUi?.asc?.globalNode ??
            colDef.meta?.sortUi?.asc?.columnNode) ||
            'A–Z'
        ),
        icon: colDef.meta?.sortUi?.asc?.icon
          ? () => colDef.meta?.sortUi?.asc?.icon as React.ReactElement
          : Icons.ArrowUp,
        onClick: () => table.getColumn(colId)?.toggleSorting?.(false),
        className: isSorted === 'asc' ? 'bg-bg-secondary' : '',
        shortcut:
          isSorted === 'asc' ? (
            <Icons.Check className="size-4 text-fg-brand" />
          ) : undefined,
      });

      items.push({
        label: String(
          (colDef.meta?.sortUi?.desc?.globalNode ??
            colDef.meta?.sortUi?.desc?.columnNode) ||
            'Z–A'
        ),
        icon: colDef.meta?.sortUi?.desc?.icon
          ? () => colDef.meta?.sortUi?.desc?.icon as React.ReactElement
          : Icons.ArrowDown,
        onClick: () => table.getColumn(colId)?.toggleSorting?.(true),
        className: isSorted === 'desc' ? 'bg-bg-secondary' : '',
        shortcut:
          isSorted === 'desc' ? (
            <Icons.Check className="size-4 text-fg-brand" />
          ) : undefined,
      });
    });

    return items;
  };

  const menu = {
    groups: [
      {
        items: createSortMenuItems(),
      },
    ],
  };

  return (
    <Dropdown
      menu={menu}
      align="start"
      contentClassName="w-[320px]"
      side="bottom"
      sideOffset={8}
    >
      <TableGlobalActionTrigger
        count={hasActiveSorts ? activeSorts.length : 0}
        onClear={clearAllSorts}
        icon={<Icons.SwitchVertical01 className="size-4" />}
        label="Sort"
      />
    </Dropdown>
  );
};

export { GlobalSortTool };
